import {
  LABOR_WELFARE,
  WORKER,
  WORKER_ADD,
  WORKER_EDIT,
  WORKER_VIEW,
  WORKER_PAYROLL,
  WRK_CATEGORY,
  WRK_DESIGNATION,
  WRK_GRADE,
  WRK_CODE_CONFIG,
  WRK_WORK_TYPE,
  WRK_LABOR_TYPE,
  WRK_MAJOR_PRODUCT,
  WRK_MACHINE,
  WRK_LEAVE_TYPE,
  WRK_LEAVE_FOR_WORKER,
  WRK_ATTENDANCE_FOR_WORKER,
  WRK_WORKER_ATTENDANCE,
  WRK_WORKER_LEAVE,
  LW_MY_REQUESTS,
  LW_PENDING_APPROVALS,
  LEGACY_WRK_LEAVE_APPROVAL,
  LEGACY_WRK_ATTENDANCE_APPROVAL,
  WRK_APPROVAL_WORKFLOW,
  WRK_DAILY_OUTPUT,
  WRK_DAILY_OUTPUT_ENTRY,
  WRK_PIECE_RATE,
  WRK_WAGE_SETTINGS,
  WRK_WAGE_SHEET,
  WRK_WAGE_SHEET_VIEW,
  WRK_ADVANCE,
  WRK_RPT_EARNINGS,
  WRK_RPT_OVERTIME,
  WRK_RPT_PRODUCTION,
  WRK_RPT_UNPAID,
} from '@constants/internal-route.constant'
import {IAppRoutes} from '@interface/common.interface'
import {guard} from './route-guard'

export const LaborWelfareRoutes: IAppRoutes = {
  link: LABOR_WELFARE,
  childrens: [
    {
      link: WORKER,
      element: guard(() => import('@modules/labor-welfare/worker'), [WORKER]),
    },
    {
      // add/edit are part of the Worker feature: adding needs its Create, editing its Update
      link: WORKER_ADD,
      element: guard(() => import('@modules/labor-welfare/worker/Form'), [WORKER], 'create'),
    },
    {
      link: WORKER_EDIT({id: ':id'}),
      element: guard(() => import('@modules/labor-welfare/worker/Form'), [WORKER], 'update'),
    },
    {
      // read-only detail view — part of the Worker feature
      link: WORKER_VIEW({id: ':id'}),
      element: guard(() => import('@modules/labor-welfare/worker/View'), [WORKER]),
    },
    {
      link: WORKER_PAYROLL,
      element: guard(() => import('@modules/labor-welfare/worker-payroll'), [WORKER_PAYROLL]),
    },
    {
      link: WRK_CATEGORY,
      element: guard(() => import('@modules/labor-welfare/category'), [WRK_CATEGORY]),
    },
    {
      link: WRK_DESIGNATION,
      element: guard(() => import('@modules/labor-welfare/designation'), [WRK_DESIGNATION]),
    },
    {
      link: WRK_GRADE,
      element: guard(() => import('@modules/labor-welfare/grade'), [WRK_GRADE]),
    },
    {
      // Company worker-code format. The back-end falls back to a built-in format when no row
      // exists, so codes are given before this screen is ever opened.
      link: WRK_CODE_CONFIG,
      element: guard(() => import('@modules/labor-welfare/worker-code'), [WRK_CODE_CONFIG]),
    },
    {
      link: WRK_WORK_TYPE,
      element: guard(() => import('@modules/labor-welfare/work-type'), [WRK_WORK_TYPE]),
    },
    {
      link: WRK_LABOR_TYPE,
      element: guard(() => import('@modules/labor-welfare/labor-type'), [WRK_LABOR_TYPE]),
    },
    {
      link: WRK_MAJOR_PRODUCT,
      element: guard(() => import('@modules/labor-welfare/major-product'), [WRK_MAJOR_PRODUCT]),
    },
    {
      link: WRK_MACHINE,
      element: guard(() => import('@modules/labor-welfare/machine'), [WRK_MACHINE]),
    },
    // Leave & Attn — HR-entered on the worker's behalf, effective at once.
    {
      link: WRK_LEAVE_TYPE,
      element: guard(() => import('@modules/labor-welfare/leave-type'), [WRK_LEAVE_TYPE]),
    },
    {
      link: WRK_LEAVE_FOR_WORKER,
      element: guard(() => import('@modules/labor-welfare/leave-for-worker'), [WRK_LEAVE_FOR_WORKER]),
    },
    {
      link: WRK_ATTENDANCE_FOR_WORKER,
      element: guard(
        () => import('@modules/labor-welfare/attendance-for-worker'),
        [WRK_ATTENDANCE_FOR_WORKER]
      ),
    },
    {
      link: WRK_WORKER_ATTENDANCE,
      element: guard(() => import('@modules/labor-welfare/worker-attendance'), [WRK_WORKER_ATTENDANCE]),
    },
    {
      // Company-wide worker leave register — gated by the seeded ACL feature LBR_WORKER_LEAVE.
      link: WRK_WORKER_LEAVE,
      element: guard(() => import('@modules/labor-welfare/worker-leave'), [WRK_WORKER_LEAVE]),
    },
    // Approvals — what HR filed above, and the queue where employees approve it (labor-service's
    // approval engine). Pending Approvals opens each row in its type's review drawer.
    {
      link: LW_MY_REQUESTS,
      element: guard(() => import('@modules/labor-welfare/approvals/MyRequests'), [LW_MY_REQUESTS]),
    },
    {
      link: LW_PENDING_APPROVALS,
      element: guard(
        () => import('@modules/labor-welfare/approvals/PendingApprovals'),
        [LW_PENDING_APPROVALS]
      ),
    },
    {link: LEGACY_WRK_LEAVE_APPROVAL, redirect: LW_PENDING_APPROVALS},
    {link: LEGACY_WRK_ATTENDANCE_APPROVAL, redirect: LW_PENDING_APPROVALS},
    {
      link: WRK_APPROVAL_WORKFLOW,
      element: guard(() => import('@modules/labor-welfare/approval-workflow'), [WRK_APPROVAL_WORKFLOW]),
    },
    // Wages — piece output per day, the piece-rate card and the company's wage rules.
    {
      link: WRK_DAILY_OUTPUT,
      element: guard(() => import('@modules/labor-welfare/daily-output'), [WRK_DAILY_OUTPUT]),
    },
    {
      // the bulk entry page is part of Daily Output: entering needs its Create
      link: WRK_DAILY_OUTPUT_ENTRY,
      element: guard(() => import('@modules/labor-welfare/daily-output/Entry'), [WRK_DAILY_OUTPUT], 'create'),
    },
    {
      link: WRK_WAGE_SHEET,
      element: guard(() => import('@modules/labor-welfare/wage-sheet'), [WRK_WAGE_SHEET]),
    },
    {
      // the sheet's own page: process, approve, pay; part of the Wage Sheets feature
      link: WRK_WAGE_SHEET_VIEW({id: ':id'}),
      element: guard(() => import('@modules/labor-welfare/wage-sheet/Detail'), [WRK_WAGE_SHEET]),
    },
    {
      link: WRK_ADVANCE,
      element: guard(() => import('@modules/labor-welfare/advance'), [WRK_ADVANCE]),
    },
    {
      link: WRK_RPT_EARNINGS,
      element: guard(() => import('@modules/labor-welfare/reports/earnings'), [WRK_RPT_EARNINGS]),
    },
    {
      link: WRK_RPT_OVERTIME,
      element: guard(() => import('@modules/labor-welfare/reports/overtime'), [WRK_RPT_OVERTIME]),
    },
    {
      link: WRK_RPT_PRODUCTION,
      element: guard(() => import('@modules/labor-welfare/reports/production'), [WRK_RPT_PRODUCTION]),
    },
    {
      link: WRK_RPT_UNPAID,
      element: guard(() => import('@modules/labor-welfare/reports/unpaid-wages'), [WRK_RPT_UNPAID]),
    },
    {
      link: WRK_PIECE_RATE,
      element: guard(() => import('@modules/labor-welfare/piece-rate'), [WRK_PIECE_RATE]),
    },
    {
      link: WRK_WAGE_SETTINGS,
      element: guard(() => import('@modules/labor-welfare/wage-settings'), [WRK_WAGE_SETTINGS]),
    },
  ],
}
